import React from "react";
import { Link } from "react-router-dom";
import { Scale, ShieldCheck } from "lucide-react";
import LegalTabs from "@/components/LegalTabs";

const Section = ({ title, id, children, testId }) => (
    <section id={id} className="scroll-mt-24" data-testid={testId}>
        <h2 className="font-heading font-medium text-2xl tracking-tight text-foreground mb-3">{title}</h2>
        <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">{children}</div>
    </section>
);

const List = ({ children }) => (
    <ul className="list-disc pl-5 space-y-1.5">{children}</ul>
);

export default function Terms() {
    return (
        <div className="bg-background" data-testid="terms-page">
            <section className="pt-16 pb-2">
                <div className="mx-auto max-w-4xl px-6">
                    <p className="overline inline-flex items-center gap-1"><Scale size={11} /> Legal</p>
                    <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl text-foreground">Terms of Service</h1>
                    <p className="mt-3 text-base text-muted-foreground max-w-2xl">
                        Baseline terms for using Simonara Talent Solutions — <b className="text-foreground">The Verified Talent Platform™</b>. Version 2026-07-15.
                    </p>
                </div>
            </section>
            <LegalTabs />

            <section className="pb-16">
                <div className="mx-auto max-w-4xl px-6 space-y-10">
                    <Section title="General Terms" id="general" testId="terms-general">
                        <p>
                            By creating an account or using Simonara Talent Solutions, you agree to these Terms of Service, our <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>, and any additional policies applicable to specific services.
                        </p>
                        <p>
                            Simonara Talent Solutions provides an AI-powered talent platform designed to connect employers and Talent. Simonara is <b className="text-foreground">not an employer, staffing agency of record, or guarantor of employment</b> unless explicitly stated in a separate written recruiting agreement.
                        </p>
                        <p>Users are responsible for providing truthful, accurate, and up-to-date information.</p>
                    </Section>

                    <Section title="Professional (Talent) Agreement" id="talent" testId="terms-talent">
                        <p className="font-semibold text-foreground" data-testid="terms-age-requirement">
                            You must be 18 or older to create a Talent Passport.
                        </p>
                        <p>By creating a Talent Passport™, you agree that:</p>
                        <List>
                            <li>You are at least 18 years old at the time of signup. Simonara Talent Solutions does not offer accounts to minors.</li>
                            <li>All information you provide is truthful and accurate — including your date of birth, which we collect once at signup to enforce the age requirement.</li>
                            <li>You have the legal right to upload your resume, certifications, portfolio, and other professional information.</li>
                            <li>You will update your Talent Passport when material information changes.</li>
                            <li>Simonara may use your profile to provide AI recommendations, career insights, and job matching.</li>
                            <li>Your Talent Passport will only be discoverable by employers according to your privacy settings.</li>
                            <li>Simonara does not guarantee interviews, job offers, or employment.</li>
                            <li>AI-generated recommendations are informational tools and should not replace your own judgment.</li>
                        </List>
                    </Section>

                    <Section title="Employer Agreement" id="employer" testId="terms-employer">
                        <p>Employers agree that they will:</p>
                        <List>
                            <li>Use the platform solely for legitimate hiring and recruiting purposes.</li>
                            <li>Maintain accurate company information.</li>
                            <li>Comply with all applicable employment, labor, anti-discrimination, and privacy laws.</li>
                            <li>Not misuse candidate information.</li>
                            <li>Not download, sell, scrape, or distribute candidate data outside legitimate recruiting activities.</li>
                            <li>Be solely responsible for all hiring decisions.</li>
                        </List>
                        <p>Simonara does not guarantee hiring outcomes or candidate performance.</p>
                    </Section>

                    <Section title="AI Services Disclaimer" id="ai" testId="terms-ai">
                        <p>Users acknowledge that:</p>
                        <List>
                            <li>AI Match™ requirement breakdowns</li>
                            <li>AI Hiring Briefs</li>
                            <li>Resume Optimization</li>
                            <li>Interview Coaching</li>
                            <li>Salary Recommendations</li>
                        </List>
                        <p>… are informational tools designed to assist users. <b className="text-foreground">Final employment decisions remain the responsibility of employers and candidates.</b> AI Match™ requirement breakdowns and AI Hiring Briefs are <b className="text-foreground">decision-support tools</b> and should not be the sole basis for hiring decisions. Simonara does not warrant that AI recommendations will always be complete, accurate, or appropriate.</p>
                    </Section>

                    <Section title="Verification Consent" id="verification" testId="terms-verification">
                        <p>Verification services are optional. Before requesting any verification, users must expressly consent. By requesting verification, users authorize Simonara Talent Solutions and its authorized verification partners to verify the information voluntarily submitted.</p>
                        <p>Verification may include:</p>
                        <List>
                            <li>Identity</li>
                            <li>Employment</li>
                            <li>Education</li>
                            <li>Professional Licenses</li>
                            <li>References</li>
                        </List>
                        <p>Verification results may appear as badges or statuses within the Talent Passport. Verification does not guarantee employment or suitability. See the <Link to="/verification-consent-policy" className="underline hover:text-foreground">Verification &amp; AI Consent Policy</Link> for full detail.</p>
                    </Section>

                    <Section title="Background Checks (Future)" id="background" testId="terms-background">
                        <p>When enabled:</p>
                        <List>
                            <li>Background checks require separate authorization.</li>
                            <li>No background check will be initiated without the candidate&apos;s explicit consent.</li>
                            <li>Employers requesting background checks agree to comply with all applicable federal, state, and local laws.</li>
                        </List>
                    </Section>

                    <Section title="Candidate Visibility" id="visibility" testId="terms-visibility">
                        <p>Professionals control whether their Talent Passport is discoverable. Users may:</p>
                        <List>
                            <li>Make their profile public to verified employers.</li>
                            <li>Limit visibility.</li>
                            <li>Disable employer discovery entirely.</li>
                        </List>
                    </Section>

                    <Section title="Employer Verification" id="employer-verification" testId="terms-employer-verification">
                        <p>Employers agree to verify their business identity through approved verification methods. Simonara may suspend or remove employer accounts suspected of fraud or misuse.</p>
                    </Section>

                    <Section title="Payments" id="payments" testId="terms-payments">
                        <List>
                            <li>Subscriptions renew automatically unless canceled.</li>
                            <li>Subscription fees are billed in advance.</li>
                            <li>Verification services are billed separately.</li>
                            <li>Recruiting services are governed by separate service agreements.</li>
                        </List>
                        <p>See the <Link to="/refund-policy" className="underline hover:text-foreground">Refund Policy</Link> for cancellation and refund details.</p>
                    </Section>

                    <Section title="Privacy" id="privacy" testId="terms-privacy">
                        <List>
                            <li>Simonara will never sell personal information.</li>
                            <li>Information is collected and processed in accordance with the <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.</li>
                            <li>Users are responsible for protecting their login credentials.</li>
                        </List>
                    </Section>

                    <Section title="Intellectual Property" id="ip" testId="terms-ip">
                        <p>All Simonara software, branding, logos, AI workflows, Talent Passport™, AI Match™, AI Hiring Brief™, and related content remain the intellectual property of Simonara Talent Solutions. Users retain ownership of the information they upload.</p>
                    </Section>

                    <Section title="Acceptable Use" id="acceptable-use" testId="terms-acceptable-use">
                        <p>Users may not:</p>
                        <List>
                            <li>Create fake accounts.</li>
                            <li>Misrepresent identity or qualifications.</li>
                            <li>Upload unlawful or infringing content.</li>
                            <li>Attempt unauthorized access.</li>
                            <li>Scrape or harvest platform data.</li>
                            <li>Spam or harass other users.</li>
                            <li>Circumvent platform security.</li>
                        </List>
                        <p>Simonara reserves the right to suspend or terminate accounts violating these Terms.</p>
                    </Section>

                    <Section title="Limitation of Liability" id="liability" testId="terms-liability">
                        <p>Simonara Talent Solutions is not liable for:</p>
                        <List>
                            <li>Employment decisions.</li>
                            <li>Hiring outcomes.</li>
                            <li>Candidate performance.</li>
                            <li>Employer actions.</li>
                            <li>AI-generated recommendations.</li>
                            <li>Delays caused by third-party services.</li>
                            <li>Verification provider errors outside Simonara&apos;s control.</li>
                        </List>
                        <p>To the fullest extent permitted by law, Simonara&apos;s liability is limited to the amount paid by the user for the applicable service giving rise to the claim.</p>
                    </Section>

                    <Section title="Modifications" id="modifications" testId="terms-modifications">
                        <p>Simonara may update these Terms periodically. Continued use of the platform constitutes acceptance of the revised Terms.</p>
                    </Section>

                    <Section title="Governing Law" id="governing-law" testId="terms-governing-law">
                        <p>These Terms shall be governed by the laws of the State of California, without regard to conflict of law principles.</p>
                    </Section>

                    <div className="card-soft p-5">
                        <div className="flex items-start gap-3">
                            <ShieldCheck size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-muted-foreground">
                                Questions? <Link to="/contact" className="underline hover:text-foreground">Contact us</Link>. For refund details see the <Link to="/refund-policy" className="underline hover:text-foreground">Refund Policy</Link>. For verification-specific consent language see the <Link to="/verification-consent-policy" className="underline hover:text-foreground">Verification &amp; AI Consent Policy</Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
